import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { getActingOnBehalfOf, leaveSharedAccount } from '../lib/sharedAccess';

const PERM_LABELS = { read: 'Read only', read_write: 'Read and write' };

/**
 * Persistent banner shown at the top of the app whenever the user is acting
 * on behalf of an account owner. Clicking "Leave" returns to their own account.
 */
export const SharedAccountBanner = () => {
  const navigate = useNavigate();
  const [acting, setActing] = useState(getActingOnBehalfOf);

  // Keep state in sync if another tab/component changes it.
  useEffect(() => {
    const sync = () => setActing(getActingOnBehalfOf());
    const interval = setInterval(sync, 1500);
    return () => clearInterval(interval);
  }, []);

  if (!acting?.owner_id) return null;

  const leave = () => {
    leaveSharedAccount();
    setActing(null);
    navigate('/choose-account', { replace: true });
  };

  return (
    <div
      className="bg-amber-wash text-amber-ink px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap text-sm"
      role="status"
      data-testid="shared-account-banner"
    >
      <p className="min-w-0 truncate">
        You&apos;re looking at <span className="font-semibold">{acting.owner_name}</span>&apos;s account
        {' · '}
        {PERM_LABELS[acting.permission] || acting.permission}
      </p>
      <button
        type="button"
        onClick={leave}
        className="inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-md text-sm font-semibold text-amber-ink hover:bg-surface/60 transition-colors duration-state"
        data-testid="shared-banner-leave"
      >
        <X className="w-4 h-4 stroke-[1.5]" aria-hidden="true" />
        Leave this account
      </button>
    </div>
  );
};

export default SharedAccountBanner;
